import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Text, ActionButton, Icon, Button } from '../custom-components';
import { IcEditPen, IcLayout, IcTrash, IcFilter } from '../custom-components/Icon';
import { useNavigate } from 'react-router-dom';
import { BsSearch, BsThreeDotsVertical } from "react-icons/bs";
import AddCategoryModal from './AddCategoryModal';
import EditCategoryModal from './EditCategoryModal';
import '../styles/cookieCategory.css';
import '../styles/dataProtectionOfficer.css';
import '../styles/pageConfiguration.css';
import '../styles/masterSetup.css';
import { languages } from '../utils/languages';
import { getCookieCategories, createCookieCategory, updateCookieCategory, getTranslations } from '../store/actions/CommonAction';
import { Slide, ToastContainer, toast } from "react-toastify";
import CustomToast from "./CustomToastContainer";

/* Category icons & colors */
const CATEGORY_STYLE = {
  Necessary:     { bg: '#E8F5E9', color: '#2E7D32', emoji: '🛡️' },
  Functional:    { bg: '#E3F2FD', color: '#1565C0', emoji: '⚙️' },
  Analytics:     { bg: '#F3E5F5', color: '#6A1B9A', emoji: '📊' },
  Advertisement: { bg: '#FFF3E0', color: '#E65100', emoji: '📣' },
  Others:        { bg: '#FCE4EC', color: '#880E4F', emoji: '···' },
};

const DEFAULT_CATEGORIES = [
  {
    category: 'Necessary',
    description: 'Essential cookies required for the website to function properly and provide core services.',
  },
  {
    category: 'Functional',
    description: 'Cookies that remember user preferences and settings to provide a personalized experience.',
  },
  {
    category: 'Analytics',
    description: 'Cookies that collect usage data to help understand and improve website performance.',
  },
  {
    category: 'Advertisement',
    description: 'Cookies used to deliver relevant advertisements based on browsing activity and interests.',
  },
  {
    category: 'Others',
    description: 'Cookies that do not belong to the defined categories or have not yet been classified.',
  },
];

const getCategoryStyle = (name = '') => {
  const key = Object.keys(CATEGORY_STYLE).find(k => name.toLowerCase().includes(k.toLowerCase()));
  return key ? CATEGORY_STYLE[key] : { bg: '#F1F5F9', color: '#475569', emoji: '🍪' };
};

const CookieCategory = () => {
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(languages[0].value);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [showHeaderTooltip, setShowHeaderTooltip] = useState(false);
  const [showDetailsTooltip, setShowDetailsTooltip] = useState(false);
  const dispatch = useDispatch();
  const multilingualConfigSaved = useSelector((state) => state.common.multilingualConfigSaved);
  const navigate = useNavigate();
  const [showMultilingualWarningModal, setShowMultilingualWarningModal] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  useEffect(() => {
    const handleClickOutside = () => setOpenDropdownId(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleLanguageChange = (e) => {
    const selectedLang = e.target.value;
    if (selectedLang === "en") { setSelectedLanguage(selectedLang); return; }
    if (!multilingualConfigSaved) { setShowMultilingualWarningModal(true); e.target.value = selectedLanguage || "en"; return; }
    setSelectedLanguage(selectedLang);
  };

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dispatch(getCookieCategories());
      let categoriesData = [];
      if (Array.isArray(response.data)) { categoriesData = response.data; }
      else if (response.data && Array.isArray(response.data.searchList)) { categoriesData = response.data.searchList; }
      else if (response.data && Array.isArray(response.searchList)) { categoriesData = response.searchList; }
      setCategories(categoriesData);
    } catch (error) { console.error("Error fetching categories:", error); setCategories([]); }
    setLoading(false);
  }, [dispatch]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const [translatedCategories, setTranslatedCategories] = useState([]);

  useEffect(() => {
    const translateCategories = async () => {
      if (!Array.isArray(categories)) { setTranslatedCategories([]); return; }
      if (selectedLanguage === 'en') { setTranslatedCategories(categories); return; }
      try {
        const translationPromises = categories.map(async (category) => {
          try {
            const translationResponse = await dispatch(getTranslations({ id: category.categoryId, language: selectedLanguage }));
            if (translationResponse?.data?.translationJson) {
              return { ...category, category: translationResponse.data.translationJson.category || category.category, description: translationResponse.data.translationJson.description || category.description };
            }
          } catch (error) { console.error(`Translation error for ${category.category}:`, error); }
          return category;
        });
        const translated = await Promise.all(translationPromises);
        setTranslatedCategories(translated);
      } catch (error) { console.error('Translation fetch error:', error); setTranslatedCategories(categories); }
    };
    translateCategories();
  }, [categories, selectedLanguage, dispatch]);

  const handleEditClick = (category) => { setSelectedCategory(category); setShowEditCategoryModal(true); };

  const handleAddCategory = async (name, description) => {
    try {
      await dispatch(createCookieCategory(name, description));
      setShowAddCategoryModal(false);
      fetchCategories();
      toast.success((props) => <CustomToast {...props} type="success" message={"Category created successfully!"} />, { icon: false });
    } catch (error) {
      toast.error((props) => <CustomToast {...props} type="error" message={error.message || "Failed to create category."} />, { icon: false });
    }
  };

  const handleUpdateCategory = async (name, description) => {
    try {
      await dispatch(updateCookieCategory(name, description));
      setShowEditCategoryModal(false);
      fetchCategories();
      toast.success((props) => <CustomToast {...props} type="success" message={"Category updated successfully!"} />, { icon: false });
    } catch (error) {
      toast.error((props) => <CustomToast {...props} type="error" message={error.message || "Failed to update category."} />, { icon: false });
    }
  };

  // Use DEFAULT_CATEGORIES as fallback when API returns no data
  const sourceCategories = (translatedCategories && translatedCategories.length > 0)
    ? translatedCategories
    : DEFAULT_CATEGORIES;

  const filteredCategories = sourceCategories.filter(cat =>
    !searchQuery ||
    (cat.category || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (cat.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="configurePage">
      <div className="bs-page">

        {/* ── Header ── */}
        <div className="bs-header-container">
          <div className="bs-header-left">
            <div className="bs-header-icon-container">
              <IcLayout height={24} width={24} color="#3B82F6" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {/* Title row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text appearance="heading-s" color="primary-grey-100">Cookie Categories</Text>
                {/* Hover tooltip i icon */}
                <div
                  style={{ position: 'relative', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                  onMouseEnter={() => setShowHeaderTooltip(true)}
                  onMouseLeave={() => setShowHeaderTooltip(false)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  {showHeaderTooltip && (
                    <div style={{ position: 'absolute', top: '24px', left: '0', zIndex: 1000, background: '#1E293B', color: '#fff', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', lineHeight: '1.5', width: '280px', boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}>
                      Cookie categories help you organize cookies based on their purpose. You can create, edit, or manage categories that are used in your consent banner and compliance reporting.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right side: button */}
          <div className="bs-header-right" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              onClick={() => setShowAddCategoryModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#3B82F6', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(59,130,246,0.3)', whiteSpace: 'nowrap' }}
            >
              + Create Category
            </button>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="bs-content">
          <div className="business-table-card" style={{ padding: '0', overflowY: 'auto', maxHeight: 'calc(100vh - 220px)', minHeight: '350px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                <tr style={{ borderBottom: '1px solid #E2E8F0', background: '#F8FAFC' }}>
                  {/* Cookie Category column */}
                  <th style={{ width: '220px', padding: '14px 20px', textAlign: 'left' }}>
                    <Text appearance="body-s-bold" color="primary-grey-80">Cookie Category</Text>
                  </th>

                  {/* Category Details column */}
                  <th style={{ padding: '14px 20px', textAlign: 'left' }}>
                    <Text appearance="body-s-bold" color="primary-grey-80">Category Details</Text>
                  </th>

                  {/* Actions column */}
                  <th style={{ width: '100px', padding: '14px 20px', textAlign: 'center' }}>
                    <Text appearance="body-s-bold" color="primary-grey-80">Actions</Text>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={3} style={{ textAlign: 'center', padding: '48px' }}><Text>Loading...</Text></td></tr>
                ) : filteredCategories.length === 0 ? (
                  <tr><td colSpan={3} style={{ textAlign: 'center', padding: '48px' }}><Text color="primary-grey-60">No categories found.</Text></td></tr>
                ) : filteredCategories.map((category, index) => {
                  const style = getCategoryStyle(category.category);
                  return (
                    <tr
                      key={index}
                      style={{ borderBottom: '1px solid #F1F5F9', transition: 'background 0.15s' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#F8FAFC'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      {/* Category name with colored emoji icon */}
                      <td style={{ padding: '16px 20px', verticalAlign: 'top' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: style.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>
                            {style.emoji}
                          </div>
                          <Text appearance="body-s-bold" color="primary-grey-100">{category.category}</Text>
                        </div>
                      </td>

                      {/* Description */}
                      <td style={{ padding: '16px 20px', verticalAlign: 'top' }}>
                        <Text appearance="body-s" color="primary-grey-80" style={{ lineHeight: '1.6' }}>{category.description}</Text>
                      </td>

                      {/* Actions */}
                      <td style={{ padding: '16px 20px', verticalAlign: 'top', textAlign: 'center' }}>
                        <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                          <button
                            className="icon-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdownId(openDropdownId === index ? null : index);
                            }}
                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '4px' }}
                          >
                            <BsThreeDotsVertical size={18} color="#64748B" />
                          </button>

                          {openDropdownId === index && (
                            <div className="action-dropdown-menu" style={{
                              position: 'absolute',
                              right: '10px',
                              top: '100%',
                              background: 'white',
                              border: '1px solid #E2E8F0',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                              zIndex: 99,
                              minWidth: '150px',
                              padding: '4px 0',
                              textAlign: 'left'
                            }}>
                              <div 
                                onClick={(e) => { e.stopPropagation(); handleEditClick(category); setOpenDropdownId(null); }}
                                style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', color: '#0F172A' }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F1F5F9'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <IcEditPen height={16} width={16} />
                                Edit Category
                              </div>
                              <div 
                                onClick={(e) => { e.stopPropagation(); setOpenDropdownId(null); }}
                                style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', color: '#EF4444' }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#FEE2E2'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <IcTrash height={16} width={16} color="#EF4444" />
                                Delete Category
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showAddCategoryModal && <AddCategoryModal onClose={() => setShowAddCategoryModal(false)} onAdd={handleAddCategory} />}
      {showEditCategoryModal && <EditCategoryModal onClose={() => setShowEditCategoryModal(false)} category={selectedCategory} onUpdate={handleUpdateCategory} />}

      {/* Multilingual Warning Modal */}
      {showMultilingualWarningModal && (
        <div className="modal-overlay" onClick={() => setShowMultilingualWarningModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Multilingual Support Required</h2>
              <button className="modal-close-btn" onClick={() => setShowMultilingualWarningModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            <div className="modal-body">
              <Text appearance="body-s" color="primary-grey-80" style={{ marginBottom: '24px' }}>
                You need to configure multilingual support in the consent configuration to enable the translate functionality.
              </Text>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <Button kind="secondary" size="medium" label="Cancel" onClick={() => setShowMultilingualWarningModal(false)} />
                <ActionButton kind="primary" size="medium" label="Go to Configuration" onClick={() => { setShowMultilingualWarningModal(false); navigate("/consent"); }} />
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="bottom-left" autoClose={3000} hideProgressBar closeOnClick pauseOnHover draggable closeButton={false} toastClassName={() => "toast-wrapper"} transition={Slide} />
    </div>
  );
};

export default CookieCategory;